import type { ProgramView } from '@shared/broadcast.ts'
import { formatJstClock } from '@shared/time.ts'
import type { PolicySlot } from '@shared/types.ts'
import { type Component, type JSX, Show } from 'solid-js'
import { slotRangeLabel, videoTypeLabel, WEEKDAY_LABELS } from '~/lib/labels'

type Props = {
  program: ProgramView
  /** サーバー時刻基準の現在時刻（ミリ秒） */
  nowMs: number
  /** いまの時間帯の編成方針（要件定義 7.2） */
  slot?: PolicySlot
  /** 次の番組（要件定義 16 章の NEXT 表示） */
  next?: ProgramView
  /** リアクションなど、番組名の脇に並べるもの */
  children?: JSX.Element
}

/**
 * NOW: いま放送中の番組。
 *
 * 狭い画面では 4 行（枠と時刻 / 番組名 / 進み具合 / 次の番組）に収める。
 * リアクションは独立した帯を持たせず、この見出しの脇に置く。
 * 次の番組もタブを分けず 1 行で添える。
 * 段が増えるほど画面が散らかって見えるため（要件定義 13 章のレスポンシブ方針）。
 */
export const NowPanel: Component<Props> = (props) => {
  const start = () => Date.parse(props.program.startAt)
  const end = () => Date.parse(props.program.endAt)

  const progress = () => {
    const span = end() - start()
    if (span <= 0) return 0
    return Math.min(100, Math.max(0, ((props.nowMs - start()) / span) * 100))
  }

  return (
    <section>
      <div class="flex items-center gap-2">
        {/*
          いまがどんな枠なのかと、その番組の放送時間帯。
          幅が足りないときに縮むのは枠名（編成方針が付ける名前なので長くなりうる）。
          時刻は桁が決まっていて縮めても得が無いので、そのまま残す
        */}
        <p class="flex min-w-0 flex-1 items-baseline gap-2 overflow-hidden text-[11px]">
          <Show when={props.slot}>
            {(slot) => (
              <span class="min-w-0 truncate font-semibold tracking-[0.14em] text-ch-accent">
                {slot().label}
              </span>
            )}
          </Show>
          <span class="shrink-0 font-mono text-ch-muted tabular">
            {formatJstClock(start())} - {formatJstClock(end())}
          </span>
          <Show when={props.slot}>
            {(slot) => (
              <span class="hidden truncate font-mono text-ch-dim tabular sm:inline">
                {WEEKDAY_LABELS[slot().day]}曜 {slotRangeLabel(slot())}
              </span>
            )}
          </Show>
        </p>

        {props.children}
      </div>

      <h3 class="mt-1 line-clamp-1 text-base font-semibold leading-snug sm:line-clamp-2 sm:text-lg">
        {props.program.title}
      </h3>

      <div class="mt-2 h-0.5 w-full overflow-hidden rounded-full bg-ch-border">
        <div
          class="h-full bg-ch-onair transition-[width] duration-1000 ease-linear"
          style={{ width: `${progress()}%` }}
        />
      </div>

      {/* 次に何が来るかは、タブを開かせずここで見せる（要件定義 16 章） */}
      <Show when={props.next}>
        {(next) => (
          <p class="mt-2 flex items-baseline gap-2 text-[11px]">
            <span class="shrink-0 font-semibold tracking-[0.14em] text-ch-dim">次</span>
            <span class="shrink-0 font-mono text-ch-muted tabular">
              {formatJstClock(Date.parse(next().startAt))}
            </span>
            <span class="truncate text-ch-muted">{next().title}</span>
          </p>
        )}
      </Show>

      {/* 縦の余白が限られるので、狭い画面では出さない */}
      <div class="mt-2 hidden flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ch-dim sm:flex">
        <span class="rounded-full border border-ch-border px-2 py-0.5">
          {videoTypeLabel(props.program.type)}
        </span>
        <span class="truncate">{props.program.channelName}</span>
      </div>

      {/* 縦の余白が限られるので、狭い画面ではタグを出さない */}
      <Show when={props.program.tags.length > 0}>
        <ul class="mt-3 hidden flex-wrap gap-1.5 lg:flex">
          {props.program.tags.slice(0, 6).map((tag) => (
            <li class="rounded bg-ch-elevated px-1.5 py-0.5 text-[11px] text-ch-muted">{tag}</li>
          ))}
        </ul>
      </Show>
    </section>
  )
}
